/**
 * Builds an ApiClient bound to the signed-in account.
 *
 * Tokens come from MSAL's cache; when the cached one has expired and cannot be renewed
 * quietly, the person is sent back through an interactive redirect.
 */

import { useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

import { ApiClient } from "../api/client";
import { apiTokenRequest } from "./msalConfig";

export function useApiClient(): ApiClient {
  const { instance, accounts } = useMsal();

  return useMemo(() => {
    const getToken = async (): Promise<string> => {
      const account = accounts[0] ?? instance.getActiveAccount();
      if (!account) {
        throw new Error("Not signed in.");
      }
      try {
        const result = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
        return result.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ ...apiTokenRequest, account });
        }
        throw error;
      }
    };
    return new ApiClient(getToken);
  }, [instance, accounts]);
}
